import { Link } from "@inertiajs/react";
import { ArrowRight, Lock, Rocket } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDay, relativeDays } from "@/lib/campaignPlanning";
import { PHASES, STEP_COPY, TIERS, displayStatus, listJoin, trialLabel } from "@/components/schools/setup/steps";
import { formatMoney } from "@/lib/format";
import { ProgressRing, StepStatusIcon, TierBadge } from "@/components/schools/setup/SetupPrimitives";

/**
 * The dashboard before launch: setup progress by stage and the one
 * thing to do next, leading into the setup guide. For a school with no
 * campaign yet, a welcome and a map of what's ahead.
 *
 * Presentational, so the help centre can show it with sample data.
 */
export default function SetupSummaryCard({ setup }) {
  const { campaign, progress, next_step: nextKey, steps, key_dates: dates, setup_path: setupPath, ready_to_launch: ready, trial } = setup;
  const next = steps.find((s) => s.key === nextKey);
  const nextCopy = STEP_COPY[nextKey];
  const withStep = (key) => `${setupPath}${setupPath.includes("?") ? "&" : "?"}step=${key}`;

  return (
    <Card className="overflow-hidden border-t-2 border-t-primary shadow-md">
      <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:p-8">
        <ProgressRing complete={progress.complete} total={progress.total} size={72} stroke={6} />
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Setup guide</p>
          <h2 className="mt-1 font-display text-2xl font-semibold text-foreground">
            {campaign ? `Getting ${campaign.name} ready` : "Let's set up your read-a-thon"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {!campaign
              ? `${steps.length} steps in five stages, in the order that works best. Your progress is saved as you go.`
              : ready
              ? `The required steps are done — it goes live on ${formatDay(dates.start_on)}.`
              : `${progress.required_remaining} required step${progress.required_remaining === 1 ? "" : "s"} left before launch.`}
            {campaign?.dates_confirmed && dates.today < dates.donations_open_on && ` Donations open ${relativeDays(dates.today, dates.donations_open_on)}.`}
          </p>
          {trial && <TrialNote trial={trial} steps={steps} />}
        </div>
      </div>

      <div className="grid border-t border-border md:grid-cols-[1.2fr_1fr]">
        {/* Up next */}
        <div className="border-b border-border p-6 md:border-b-0 md:border-r md:p-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {nextKey === "launch" && ready ? "All set" : "Up next"}
          </p>
          <div className="mt-3 flex items-start gap-3">
            {nextKey === "launch" ? (
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
                <Rocket className="h-3.5 w-3.5" />
              </span>
            ) : (
              <StepStatusIcon status={next.status} number={steps.indexOf(next) + 1} />
            )}
            <div className="min-w-0 space-y-2">
              <p className="font-medium text-foreground">{nextCopy.title}</p>
              {nextKey !== "launch" && <TierBadge tier={next.tier} />}
              <p className="text-sm leading-relaxed text-muted-foreground">{nextCopy.why}</p>
            </div>
          </div>
          <Button className="mt-5" asChild>
            <Link href={withStep(nextKey)}>
              {nextKey === "launch" ? "See the launch checklist" : campaign ? "Continue setup" : "Start setup"}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* Stages */}
        <ol className="space-y-3 p-6 md:p-8">
          {PHASES.filter((phase) => phase.key !== "launch").map((phase) => {
            const phaseSteps = steps.filter((s) => s.phase === phase.key);
            const complete = phaseSteps.filter((s) => s.status === "done" || s.status === "skipped").length;
            const finished = complete === phaseSteps.length;
            return (
              <li key={phase.key}>
                <Link href={withStep(phaseSteps.find((s) => s.status !== "done" && s.status !== "skipped")?.key || phaseSteps[0].key)} className="group flex items-center gap-3">
                  <StepStatusIcon status={finished ? "done" : "todo"} number={PHASES.indexOf(phase) + 1} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className={cn("block text-sm font-medium group-hover:text-primary", finished ? "text-muted-foreground" : "text-foreground")}>{phase.title}</span>
                    <span className="block text-xs text-muted-foreground">{phase.description}</span>
                  </span>
                  <span className="text-xs text-muted-foreground font-tabular">{complete}/{phaseSteps.length}</span>
                </Link>
              </li>
            );
          })}
        </ol>
      </div>

      <p className="border-t border-border bg-background/50 px-6 py-3 text-xs text-muted-foreground md:px-8">
        {Object.entries(TIERS).map(([tier, { label, description }]) => `${label}: ${description.toLowerCase()}`).join(" · ")}
      </p>
    </Card>
  );
}

// On the free trial: which steps the campaign fee unlocks, and how to pay.
function TrialNote({ trial, steps }) {
  const locked = steps.filter((s) => displayStatus(s) === "locked").map((s) => (s.key === "square" ? "Square" : STEP_COPY[s.key].short.toLowerCase()));
  // Launch always waits on the fee, so there's at least one.
  const list = listJoin(locked) || "launch";
  const unlocks = `${list[0].toUpperCase()}${list.slice(1)} ${locked.length > 1 ? "unlock" : "unlocks"}`;

  return (
    <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground">
      <Badge variant="warning" className="gap-1">
        <Lock className="h-3 w-3" aria-hidden="true" />
        {trialLabel(trial)}
      </Badge>
      <span>
        {trial.plan
          ? `${unlocks} once you pay the ${formatMoney(trial.plan.price_cents)} campaign fee.`
          : "Set everything up for free. You pay the campaign fee before it goes live."}
      </span>
      {trial.payment_path && (
        <Link href={trial.payment_path} className="font-medium text-primary hover:underline underline-offset-4">
          How to pay
        </Link>
      )}
    </p>
  );
}
